import { z } from "zod";
import { getArtifactResp } from "./artifacts.ts";
import { messageMode } from "./messageConstants.ts";
import { KNOWN_ACTIONS } from "./messageLinks.ts";
// TODO: all of these should be converted to pure types, we should not use zod for the server->client network boundary.

// Contacts
export const handle = z.object({
  id: z.string(),
  value: z.string(),
  type: z.enum(["email", "phone", "home", "work"]),
});

export const messageAuthor = z.object({
  urn: z.string(),
  name: z.string(),
  picture: z.string().optional(),
  type: z.enum(["person", "company", "assistant", "user"]),
  handles: z.array(handle).optional(),
});

export type MessageAuthor = z.infer<typeof messageAuthor>;

// Legacy debug structures for recursive AI scope tracking.
// @deprecated Used only to hydrate `meta.debug.{aiScope,externalChannel}` on
//   messages written before the thread-events "Events" view shipped; new
//   traces go through `thread_events`. Final teardown tracked in BIG-3887.
export const aiDebugScopeAction = z.object({
  label: z.string(),
  description: z.string().optional(),
  longContent: z.string().optional(),
  json: z.any().optional(),
  code: z
    .object({
      language: z.string(),
      code: z.string(),
    })
    .optional(),
  ai: z
    .object({
      thought: z.string().optional(),
      examples: z.array(z.unknown()).optional(),
    })
    .optional(),
  forthic: z
    .object({
      modules: z.array(z.string()).optional(),
      words: z.array(z.string()).optional(),
      code: z.string(),
    })
    .optional(),
  durationMs: z.number().optional(),
});

export const aiDebugScope = z.object({
  label: z.string(),
  body: z.string().optional(),
  isRoot: z.boolean().optional(),
  status: z
    .object({
      request: z.string().optional(),
      statusMessage: z.string(),
      status: z.enum(["success", "partial_success", "failure"]),
    })
    .optional(),
  forthicStack: z
    .object({
      urn: z.string(),
      description: z.string(),
      data: z.array(z.unknown()),
    })
    .optional(),
  actions: z.array(aiDebugScopeAction).optional(),
  get scopes() {
    return z.array(aiDebugScope).optional();
  },
});

/**
 * @deprecated Legacy debug-panel shape from before the thread-events "Events"
 *   view existed. New writes go through `thread_events`; this Zod type only
 *   exists so the admin debug panel can validate the `meta.debug.{aiScope,externalChannel}`
 *   blobs on messages written before that view shipped. Final teardown
 *   tracked in BIG-3887.
 */
export type AiDebugScopeAction = z.infer<typeof aiDebugScopeAction>;
/**
 * @deprecated Legacy debug-panel shape from before the thread-events "Events"
 *   view existed. New writes go through `thread_events`; this Zod type only
 *   exists so the admin debug panel can validate the `meta.debug.{aiScope,externalChannel}`
 *   blobs on messages written before that view shipped. Final teardown
 *   tracked in BIG-3887.
 */
export type AiDebugScope = z.infer<typeof aiDebugScope>;

// Debug information for messages (admin only)
export const messageDebug = z.object({
  /** @deprecated Ancient per-message plan markdown — not written for a long time.
   *   Only hydrated for old rows that still have `msg.plan`. Final teardown: BIG-3887. */
  plan: z.string().optional(),
  /** @deprecated Traces moved to inference thread-events (rendered in the "Events" view).
   *   No longer written to messages; only hydrated for older rows that still carry
   *   `msg.traceIds`. Final teardown: BIG-3887. */
  traceIds: z.array(z.string()).optional(),
  /** @deprecated Use scopes instead. Kept for backwards compatibility with debug links responses. Final teardown: BIG-3887. */
  aiScope: aiDebugScope.optional(),
  /**
   * Flattened array of all debug scopes (aiScope, externalChannel, etc.).
   * @deprecated Legacy debug-panel data sourced from `meta.debug` on messages
   *   written before the thread-events "Events" view shipped. Final teardown:
   *   BIG-3887.
   */
  scopes: z.array(aiDebugScope).optional(),
});

export type MessageDebug = z.infer<typeof messageDebug>;

export const autoMessageTypes = z.enum([
  // Auto Messages
  "autoLearnMore",
  "autoReply",
  "autoReplyAll",
  "autoForward",
  "autoMemoryConfirmed",

  // RSVP Actions
  "autoAcceptRsvp",
  "autoDeclineRsvp",
  "autoTentativeRsvp",

  // Email Actions
  "autoSendEmailDraft",
  "autoCancelEmailDraft",

  // Invitation Actions
  "autoSendInvitation",
  "autoCancelInvitation",

  // Event Change Actions
  "autoApplyEventChange",
  "autoCancelEventChange",

  // Contact Change Actions
  "autoApplyContactChange",
  "autoCancelContactChange",

  // Phone Call Actions
  "autoMakePhoneCall",
  "autoCancelPhoneCall",

  // Brief Actions
  "autoExecuteBrief",
  "autoCancelBrief",

  // Ask User Form
  "autoAskUserFormSubmit",

  // Scheduling Actions
  "autoApproveScheduling",
  "autoCounterProposeScheduling",
  "autoCancelScheduling",

  // Scheduling Kickoff Actions
  "autoApproveSchedulingKickoff",
  "autoCancelSchedulingKickoff",

  // Routine Actions
  "autoApproveRoutine",
  "autoCancelRoutine",

  // Document Actions
  "autoApproveDocument",

  // Skill Actions
  "autoApproveSkill",
  "autoCancelSkill",

  // Applet Actions
  "autoApproveApplet",

  // Task Actions — the user closing or reopening one of their own tasks from
  // the task list, which is a different surface from the thread it belongs to.
  "autoTaskCompleted",
  "autoTaskCancelled",
  "autoTaskReopened",

  // External message (Slack / Teams) send approvals
  "autoSendExternalMessage",
  "autoCancelExternalMessage",

  // OAuth Actions
  "autoOAuthConnected",
  "autoConnectorPickerDone",
  "autoPickerDone",
  "autoOAuthCancelled",

  // Agent CC — synthetic engagement signal from handleAgentCC. Renders as a
  // user-side auto-message bubble and surfaces to the actor as a user-role
  // turn (see shared/client/autoActionText.ts for the breadcrumb copy).
  "autoCommunicationReceived",

  // The user putting an emoji on one of the agent's messages. Drawn as a badge
  // on that message, never as a row; the agent reads it as feedback.
  "autoReaction",
]);
export type AutoMessageType = z.infer<typeof autoMessageTypes>;

// User message types
export const userMessageTypes = z.enum([
  // User Messages
  "userRequest",

  // Instruction text okthx authored for itself and filed as an inbound turn
  // (routine prompt, skill directive, spawned-thread seed, relayed directive).
  // A user message type despite the name: it renders user-side like a
  // `userRequest`, and the distinction it carries is authorship, which matters
  // to memory extraction. See the docstring in `api/src/schemas/messages.ts`.
  "agentRequest",

  ...autoMessageTypes.options,
]);
export type UserMessageType = z.infer<typeof userMessageTypes>;

// Agent message types
export const agentMessageTypes = z.enum([
  // @deprecated("Use One of the other message types")
  "topic",

  // Status Messages @deprecated
  /** @deprecated */
  "statusUpdate",
  /** @deprecated */
  "statusComplete",

  // Agent Messages
  "agentPlan",
  "agentResponse",
  "agentQuestion",
  "agentResponseError",
  /** @deprecated - going forward we will use agentResponse instead */
  "evaluatorResponse",
  /** @deprecated */
  "emailSummary",
  "agent",
  "event",

  // External Communications
  "externalCommunication",

  // Scheduling Suggestions
  "suggestScheduling",
]);
export type AgentMessageType = z.infer<typeof agentMessageTypes>;

export const chatMessageTypes = z.enum([
  // User Messages
  ...userMessageTypes.options,

  // Agent Messages
  ...agentMessageTypes.options,
]);
export type ChatMessageType = z.infer<typeof chatMessageTypes>;

/**
 * Per-message state the client owns and reports back — facts only a renderer can
 * know, because they are about what actually reached the reader.
 *
 * Every field is optional and additive: an older client that has never heard of
 * a key simply leaves it alone, and the server merges rather than replaces.
 */
export const messageClientState = z.object({
  /**
   * `action:` links in this message whose one-shot side effect has already run.
   * A renderer plays an action only when its id is absent here, which is what
   * keeps a celebration from repeating on a refresh or on a second device.
   */
  firedActions: z.array(z.enum(KNOWN_ACTIONS)).optional(),
});

export type MessageClientState = z.infer<typeof messageClientState>;

export const getMessageResp = z.object({
  content: z.string(),
  id: z.number(),
  threadId: z.number(),
  type: chatMessageTypes,
  status: z.enum(["completed", "inProgress", "processed", "queued", "aborted"]),
  isRead: z.boolean(),
  labels: z.array(z.string()).optional(),
  artifacts: z.array(getArtifactResp).optional(),
  author: messageAuthor.optional(),
  needsReply: z.boolean().optional(),
  mode: messageMode.optional(),
  shouldDisplayContent: z.boolean().optional(),
  /**
   * On a `autoReaction`, the agent reply it is the user's emoji on; the emoji
   * is `content`. Absent on every other type.
   */
  replyToMessageId: z.number().optional(),
  debug: messageDebug.optional(),
  clientState: messageClientState.optional(),
});

export type GetMessageResp = z.infer<typeof getMessageResp>;

/**
 * Record that a message's action has run. Sent by whichever surface actually
 * played it, since only the client knows the effect reached the user.
 */
export const markMessageActionFiredReq = z.object({
  messageId: z.number().int().positive(),
  action: z.enum(KNOWN_ACTIONS),
});

export type MarkMessageActionFiredReq = z.infer<typeof markMessageActionFiredReq>;
